import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Plus, Save, RotateCcw, Loader2, Trash2, Gift, Percent, PoundSterling, Tag } from "lucide-react";

const TYPE_OPTIONS = [
  { value: "percent_off", label: "Percent off bundle", icon: Percent },
  { value: "flat_off",    label: "Flat £ off bundle",  icon: PoundSterling },
  { value: "free_addon",  label: "Free add-on item",   icon: Gift },
];

function emptyBundle() {
  return {
    id: undefined,
    name: "",
    type: "percent_off",
    categories: [],
    min_qty: 1,
    discount_pct: 10,
    discount_amount: 0,
    gives_free: { name: "", category: "", price: 0 },
    active: true,
  };
}

export default function BundlesManagerPage() {
  const [bundles, setBundles] = useState([]);
  const [categories, setCategories] = useState([]); // [{key, label}]
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/workshop/bundles");
      setBundles(r.data.bundles || []);
      setCategories(r.data.categories || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load bundles");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    // Light client-side validation
    for (const b of bundles) {
      if (!b.name?.trim()) return toast.error("Each bundle needs a name");
      if ((b.categories || []).length === 0) return toast.error(`"${b.name}" needs at least one category`);
      if (b.type === "free_addon" && !(b.gives_free?.name)) return toast.error(`"${b.name}" needs a free add-on name`);
    }
    setSaving(true);
    try {
      const r = await api.put("/workshop/bundles", bundles);
      setBundles(r.data.bundles || []);
      toast.success("Bundles saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally { setSaving(false); }
  };

  const resetDefaults = async () => {
    if (!confirm("Reset to the 3 starter bundles? Any custom ones will be lost.")) return;
    try {
      const r = await api.post("/workshop/bundles/reset-defaults");
      setBundles(r.data.bundles || []);
      toast.success("Defaults restored");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    }
  };

  const update = (idx, patch) =>
    setBundles((b) => b.map((row, i) => (i === idx ? { ...row, ...patch } : row)));

  const remove = (idx) =>
    setBundles((b) => b.filter((_, i) => i !== idx));

  const add = () => setBundles((b) => [...b, emptyBundle()]);

  const toggleCategory = (idx, cat) => {
    setBundles((b) =>
      b.map((row, i) => {
        if (i !== idx) return row;
        const has = (row.categories || []).includes(cat);
        return { ...row, categories: has ? row.categories.filter((c) => c !== cat) : [...(row.categories || []), cat] };
      })
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard?tab=manager" className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground" data-testid="bundles-back">
            <ArrowLeft className="w-4 h-4" /> Back to dashboard
          </Link>
          <div className="flex items-center gap-2">
            <button onClick={resetDefaults} className="btn-outline text-sm" data-testid="bundles-reset-defaults">
              <RotateCcw className="w-3.5 h-3.5" /> Reset to defaults
            </button>
            <button onClick={save} disabled={saving} className="btn-primary text-sm" data-testid="bundles-save-all">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? "Saving…" : "Save all"}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        <div>
          <div className="label-overline">Customer-facing promotions</div>
          <h1 className="font-display text-3xl font-black tracking-tight mt-1">Bundle deals</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl leading-relaxed">
            When the customer approves work on their quote, ServiceSell PRO checks every bundle below.
            Matching bundles fire automatically — the discount drops in front of the customer's eyes as they tick approvals.
            All three rule types are supported: <strong className="text-foreground">% off</strong>, <strong className="text-foreground">flat £ off</strong>, or a <strong className="text-foreground">free add-on item</strong>.
          </p>
        </div>

        {loading ? (
          <div className="text-center text-muted-foreground py-16"><Loader2 className="w-5 h-5 animate-spin inline-block" /> Loading…</div>
        ) : (
          <div className="space-y-4" data-testid="bundles-list">
            {bundles.map((b, idx) => (
              <BundleCard
                key={b.id || idx}
                bundle={b}
                idx={idx}
                categories={categories}
                onChange={(patch) => update(idx, patch)}
                onRemove={() => remove(idx)}
                onToggleCategory={(cat) => toggleCategory(idx, cat)}
              />
            ))}

            <button onClick={add} className="w-full card-default p-5 border-dashed border-2 hover:border-foreground/40 transition-all flex items-center justify-center gap-2 text-muted-foreground hover:text-foreground" data-testid="bundles-add">
              <Plus className="w-4 h-4" /> Add another bundle
            </button>
          </div>
        )}
      </main>
    </div>
  );
}

function BundleCard({ bundle, idx, categories, onChange, onRemove, onToggleCategory }) {
  const TypeIcon = TYPE_OPTIONS.find((t) => t.value === bundle.type)?.icon || Tag;
  return (
    <div className={`card-default p-5 ${bundle.active ? "" : "opacity-60"}`} data-testid={`bundle-card-${idx}`}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-primary/15 grid place-items-center shrink-0">
          <TypeIcon className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0 space-y-4">
          {/* Name + active toggle + delete */}
          <div className="flex items-center gap-3">
            <input
              type="text"
              value={bundle.name}
              onChange={(e) => onChange({ name: e.target.value })}
              placeholder="Bundle name (e.g. Air-con & geo combo)"
              className="input-default flex-1 font-semibold"
              data-testid={`bundle-name-${idx}`}
            />
            <label className="inline-flex items-center gap-1.5 cursor-pointer text-xs font-bold uppercase tracking-wider text-muted-foreground select-none">
              <input
                type="checkbox"
                checked={!!bundle.active}
                onChange={(e) => onChange({ active: e.target.checked })}
                className="w-4 h-4"
                data-testid={`bundle-active-${idx}`}
              />
              Active
            </label>
            <button onClick={onRemove} className="text-muted-foreground hover:text-destructive p-1" title="Delete bundle" data-testid={`bundle-remove-${idx}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </div>

          {/* Type selector */}
          <div className="grid sm:grid-cols-3 gap-2">
            {TYPE_OPTIONS.map((opt) => {
              const Icon = opt.icon;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => onChange({ type: opt.value })}
                  className={`p-2 rounded-md border text-xs font-bold inline-flex items-center justify-center gap-1.5 transition-all ${
                    bundle.type === opt.value
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-muted-foreground hover:border-foreground/40"
                  }`}
                  data-testid={`bundle-type-${opt.value}-${idx}`}
                >
                  <Icon className="w-3.5 h-3.5" /> {opt.label}
                </button>
              );
            })}
          </div>

          {/* Type-specific fields */}
          {bundle.type === "percent_off" && (
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">% off bundle items</label>
              <div className="relative max-w-[120px] mt-1">
                <input
                  type="number" min="0" max="100" step="0.5"
                  value={bundle.discount_pct ?? 0}
                  onChange={(e) => onChange({ discount_pct: Number(e.target.value) })}
                  className="input-default pr-7"
                  data-testid={`bundle-pct-${idx}`}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
              </div>
            </div>
          )}
          {bundle.type === "flat_off" && (
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Flat £ off bundle items</label>
              <div className="relative max-w-[140px] mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">£</span>
                <input
                  type="number" min="0" step="0.01"
                  value={bundle.discount_amount ?? 0}
                  onChange={(e) => onChange({ discount_amount: Number(e.target.value) })}
                  className="input-default pl-7"
                  data-testid={`bundle-flat-${idx}`}
                />
              </div>
            </div>
          )}
          {bundle.type === "free_addon" && (
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Free add-on details</label>
              <div className="grid sm:grid-cols-[2fr_1fr] gap-2">
                <input
                  type="text"
                  value={bundle.gives_free?.name || ""}
                  onChange={(e) => onChange({ gives_free: { ...(bundle.gives_free || {}), name: e.target.value } })}
                  placeholder="e.g. 4-wheel alignment"
                  className="input-default"
                  data-testid={`bundle-free-name-${idx}`}
                />
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">£</span>
                  <input
                    type="number" min="0" step="0.01"
                    value={bundle.gives_free?.price ?? 0}
                    onChange={(e) => onChange({ gives_free: { ...(bundle.gives_free || {}), price: Number(e.target.value) } })}
                    placeholder="Retail value"
                    className="input-default pl-7"
                    data-testid={`bundle-free-price-${idx}`}
                  />
                </div>
              </div>
            </div>
          )}

          {/* min_qty: only really meaningful on free_addon ("when 4 tyres approved") */}
          {bundle.type === "free_addon" && (
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Trigger minimum quantity</label>
              <input
                type="number" min="1" step="1"
                value={bundle.min_qty || 1}
                onChange={(e) => onChange({ min_qty: Number(e.target.value) })}
                className="input-default mt-1 max-w-[100px]"
                data-testid={`bundle-minqty-${idx}`}
              />
              <p className="text-[11px] text-muted-foreground mt-1">e.g. set to 4 for "4 tyres unlocks free alignment".</p>
            </div>
          )}

          {/* Category picker */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              {bundle.type === "free_addon" ? "Bundle is triggered when these categories are approved" : "Bundle items (customer must approve at least one from each)"}
            </label>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {categories.map((c) => {
                const selected = (bundle.categories || []).includes(c.key);
                return (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => onToggleCategory(c.key)}
                    className={`px-2.5 py-1 rounded-full text-xs font-bold border transition-all ${
                      selected
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-muted/20 text-muted-foreground border-border hover:border-foreground/40"
                    }`}
                    data-testid={`bundle-cat-toggle-${c.key}-${idx}`}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
